import { JSX, FC, useState } from "react";
import { useSelector } from "react-redux";
import { useRouter } from "next/router";
import { useFormik, FormikProvider, FieldArray, FormikProps } from "formik";
import { RedirectableProvider } from "next-auth/client";
import { signIn } from "src/utils/nextAuth";
import prepareNextAuthErrorMessage from "src/utils/prepareNextAuthErrorMessage";
import { AuthProvider } from "pages/api/auth/getUserTokenFromApi";
import { t, Trans } from "@lingui/macro";
import { Text } from "@otrium/atoms";
import { Box, Flex, useBreakpoints } from "@otrium/core";
import { useTheme, Theme } from "@emotion/react";
import { Gender, getLocaleGenderIds } from "src/modules/page";
import { googleTagManager } from "src/tracking";
import { isValidEmail } from "src/utils/validations";
import { useImageError } from "src/hooks/useImageError";
import Input from "src/atoms/Input";
import { SocialButton } from "src/atoms/SocialButton";
import { PageHead } from "src/molecules/PageHead";
import { SalesLandingCarousel } from "src/molecules/SalesLandingCarousel";
import { LogoRoundedHeader } from "src/molecules/LogoRoundedHeader";
import { SingleSalesLanding } from "src/types/graphql.d";
import {
  StyledTopSection,
  StyledTopSectionContent,
  StyledBrandLogo,
  StyledTitle,
  StyledSocialButtonsContainer,
  StyledGenderIdButton,
  StyledButtonWrapper,
  StyledLoginLink,
  BlackGradient,
  SeoBottomTextWrapper,
  StyledRegisterButton,
} from "./SalesLandingViewSelectedBrands.styled";
import { Modal } from "src/atoms/Modal";
import { ScreenLoader } from "src/molecules/ScreenLoader";
import { formatName } from "src/utils/formatName";
import FORM_ERROR_MESSAGES from "src/messages/form";
import { isClient } from "src/utils/isClient";
import { sanitize } from "isomorphic-dompurify";
import { useLingui } from "@lingui/react";
import { shopTypeTranslation } from "src/translations/shopTypeTranslation";

interface FormikValues {
  email: string;
  shopPreferences: Gender[];
}

interface FormikErrors {
  email?: string;
  shopPreferences?: string;
}

interface Props {
  salesLanding: SingleSalesLanding;
  genderId?: Gender;
  brandSlug?: string;
}

const SalesLandingViewSelectedBrands: FC<Props> = ({
  salesLanding,
  genderId: defaultGenderId,
  brandSlug,
}): JSX.Element => {
  const router = useRouter();
  const [authError, setAuthError] = useState<string | undefined>("");

  const { women, men } = useSelector(getLocaleGenderIds);
  const theme: Theme = useTheme();
  const { query } = router;
  const openScreenLoader = query.signin;

  const [setImg, hasImgError] = useImageError();
  const { isMobile } = useBreakpoints();
  const { _, i18n } = useLingui();
  const formik: FormikProps<FormikValues> = useFormik<FormikValues>({
    initialValues: {
      email: "",
      shopPreferences: (defaultGenderId ? [defaultGenderId] : []) as Gender[],
    },
    validate: (values) => {
      const errors: FormikErrors = {};
      if (!isValidEmail(values.email)) {
        errors.email = _(FORM_ERROR_MESSAGES.email);
      }
      return errors;
    },
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      setAuthError(undefined);

      const response = await signIn(
        AuthProvider.CREDENTIALS_SOFT_LOGIN as RedirectableProvider,
        {
          assumedAuthType: "sign_up",
          ...values,
          redirect: false,
        }
      );

      if (response?.url) {
        // NOTE: Assumes that 'response.url' is the same as current URL with '?signin=true'. Push
        // it with { shallow: true } to not run 'getInitialProps' of '/outlet/...' page. Check comments
        // in '/outlet/...' for more details
        void router.push(response.url, undefined, { shallow: true });
      } else if (response?.error) {
        setAuthError(prepareNextAuthErrorMessage(response.error));
      }
    },
  });

  const { saleslanding_data, seo_texts } = salesLanding;

  return (
    <Box>
      {openScreenLoader === "true" && (
        <Modal onClose={() => {}} overFlow={"visible"} isOpen={true}>
          <ScreenLoader />
        </Modal>
      )}
      <PageHead
        title={formatName(seo_texts.seo_title)}
        description={formatName(seo_texts.seo_description)}
        noindex={Boolean(saleslanding_data.is_noindex)}
      />
      <Box sx={{ position: "relative" }}>
        <LogoRoundedHeader />

        <StyledTopSection
          url={
            isMobile
              ? saleslanding_data.top_image_mobile
              : saleslanding_data.top_image
          }
        >
          <StyledTopSectionContent>
            {saleslanding_data.logo && !hasImgError ? (
              <StyledBrandLogo ref={setImg} src={saleslanding_data.logo} />
            ) : (
              <StyledTitle>{formatName(saleslanding_data.title)}</StyledTitle>
            )}
            <Text
              fontSize={3}
              fontWeight="bold"
              lineHeight="1.33"
              letterSpacing="1.6px"
              textAlign="center"
              color={theme.colors.tone.white}
              mb={[2, 2, 2, 3]}
              sx={{ textTransform: "uppercase" }}
            >
              {saleslanding_data?.outlet_label}
            </Text>
            <Text
              fontFamily="CharisSILW"
              fontSize={[3, 3, 3, 4]}
              fontWeight="bold"
              lineHeight="1.33"
              letterSpacing="normal"
              textAlign="center"
              color={theme.colors.tone.white}
              mb={[3, 3, 3, 5]}
            >
              {saleslanding_data?.discount_text}
            </Text>
            <Box maxWidth={["100%", "100%", "352px"]} mx={[5, 5, "auto"]}>
              <Box>
                <form noValidate onSubmit={formik.handleSubmit}>
                  <Input
                    mb={2}
                    backgroundColor="White"
                    type="text"
                    isError={Boolean(formik.errors?.email)}
                    placeholder={t(i18n)`Email address`}
                    {...formik.getFieldProps("email")}
                  />

                  {authError && (
                    <Text mb={2} color="semantic.red">
                      {authError}
                    </Text>
                  )}
                  {formik.errors?.email && (
                    <Text
                      fontSize={0}
                      fontWeight={600}
                      lineHeight={1.43}
                      letterSpacing="normal"
                      textAlign="left"
                      color={"semantic.red"}
                      mb={3}
                    >
                      <Trans>{formik.errors?.email}</Trans>
                    </Text>
                  )}

                  {!defaultGenderId && (
                    <Flex mb={2}>
                      <FormikProvider value={formik}>
                        <FieldArray
                          name="shopPreferences"
                          render={(arrayHelpers) => (
                            <>
                              {[women, men].map((gender, index) => (
                                <StyledGenderIdButton
                                  key={gender}
                                  type="button"
                                  marginLeft={index > 0}
                                  active={formik.values.shopPreferences.includes(
                                    gender as Gender
                                  )}
                                  onClick={() =>
                                    formik.values.shopPreferences.includes(
                                      gender as Gender
                                    )
                                      ? arrayHelpers.remove(
                                          formik.values.shopPreferences.findIndex(
                                            (shopPreference) =>
                                              shopPreference === gender
                                          )
                                        )
                                      : arrayHelpers.push(gender)
                                  }
                                >
                                  {_(shopTypeTranslation[gender])}
                                </StyledGenderIdButton>
                              ))}
                            </>
                          )}
                        />
                      </FormikProvider>
                    </Flex>
                  )}

                  <StyledButtonWrapper>
                    <StyledRegisterButton
                      fullWidth
                      type="submit"
                      variant="tertiary"
                      colorScheme="light"
                      disabled={!formik.values.shopPreferences.length}
                    >
                      <Trans>Register</Trans>
                    </StyledRegisterButton>
                  </StyledButtonWrapper>
                </form>
              </Box>
              <StyledSocialButtonsContainer>
                <SocialButton
                  type="facebook"
                  fullWidth
                  onClick={() =>
                    void signIn(AuthProvider.FACEBOOK, {
                      assumedAuthType: "sign_up",
                    })
                  }
                />
                <SocialButton
                  type="google"
                  fullWidth
                  onClick={() =>
                    void signIn(AuthProvider.GOOGLE, {
                      assumedAuthType: "sign_up",
                    })
                  }
                />
                {/* [TODO:restore the button once Apple login is fixed] */}
                {/* <SocialButton
                  type="apple"
                  onClick={() => signIn(AuthProvider.APPLE)}
                /> */}
              </StyledSocialButtonsContainer>

              <Text
                color="White"
                fontSize="16px"
                mt={2}
                mb={2}
                sx={{ "&:first-letter": { textTransform: "uppercase" } }}
              >
                <Trans>already registered?</Trans>{" "}
                <Box as="a" href="/sign-in/">
                  <StyledLoginLink
                    onClick={() => googleTagManager.trackAlreadyRegistered()}
                  >
                    <Trans>Login</Trans>
                  </StyledLoginLink>
                </Box>
              </Text>
            </Box>
          </StyledTopSectionContent>
        </StyledTopSection>
      </Box>
      <BlackGradient />
      {isClient && brandSlug && <SalesLandingCarousel brandSlug={brandSlug} />}
      {seo_texts?.seo_bottom_text_gate_3_0 && (
        <Box
          backgroundColor={theme.colors.tone.black}
          pt={["48px", "48px", "48px", "48px", "96px"]}
          pb="48px"
        >
          <SeoBottomTextWrapper
            dangerouslySetInnerHTML={{
              __html: sanitize(seo_texts?.seo_bottom_text_gate_3_0),
            }}
            maxWidth={["1120px"]}
            mx={[4, 4, 4, 4, 4, "auto"]}
          />
        </Box>
      )}
    </Box>
  );
};

export default SalesLandingViewSelectedBrands;
